/**
 * Title-based cohorts for pricing insight: narrow comps when the query title
 * contains a comparable signal (screen size, KVA, HP, litres, bed size, kg,
 * seating capacity, fan inches, audio watts, etc.).
 */

import { FURNITURE_SURFACE_SUBCATEGORY_IDS } from "@/lib/listing/furnitureSurfaceListingSpec";
import {
  CANONICAL_FAN_SUBCATEGORY_ID,
  FAN_BLADE_SIZE_INCHES_MAX,
  FAN_BLADE_SIZE_INCHES_MIN,
  LEGACY_FAN_SUBCATEGORY_IDS,
} from "@/lib/listing/fanListingSpec";
import {
  CANONICAL_MICROWAVE_SUBCATEGORY_ID,
  LEGACY_MICROWAVE_SUBCATEGORY_IDS,
} from "@/lib/listing/applianceInverterListingSpec";

/** Minimum approved listings required to prefer the cohort over the full subcategory. */
export const MIN_TITLE_COHORT_ITEMS = 5;

/** ± litres when exact L cohort is thin (Phase 5 — fridges, freezers). */
export const TITLE_COHORT_FUZZY_LITERS_TOLERANCE = 10;

/** Fridge / freezer / cooler capacity bounds (shared `liters` extract). */
export const TITLE_COHORT_LITERS_MIN = 10;
export const TITLE_COHORT_LITERS_MAX = 500000;

/** Microwave capacity bounds (same kind; family-scoped). */
export const TITLE_COHORT_MICROWAVE_LITERS_MIN = 10;
export const TITLE_COHORT_MICROWAVE_LITERS_MAX = 50;

/** ± KVA when exact generator cohort is thin. */
export const TITLE_COHORT_FUZZY_KVA_TOLERANCE = 0.5;

/** ± HP when exact AC cohort is thin. */
export const TITLE_COHORT_FUZZY_HP_TOLERANCE = 0.25;

/** ± kg when exact washer cohort is thin. */
export const TITLE_COHORT_FUZZY_KG_TOLERANCE = 1;

/**
 * Relative Near band for audio watts (Phase 0 lock D6).
 * Match when |found − query| / query ≤ this fraction.
 */
export const TITLE_COHORT_NEAR_WATTS_RELATIVE = 0.2;

/** ± seats for soft Near when exact seater cohort is thin (D5). */
export const TITLE_COHORT_NEAR_SEATER_TOLERANCE = 1;

/** ± inches for soft Near when exact fan size cohort is thin (D5). */
export const TITLE_COHORT_NEAR_FAN_INCHES_TOLERANCE = 2;

/** Seating capacity bounds (align `chairListingSpec` total capacity). */
export const TITLE_COHORT_SEATER_MIN = 1;
export const TITLE_COHORT_SEATER_MAX = 12;

/** Audio power bounds (consumer sound systems / speakers / soundbars). */
export const TITLE_COHORT_WATTS_MIN = 10;
export const TITLE_COHORT_WATTS_MAX = 20000;

export type TitleCohortMatchMode = "exact" | "fuzzy" | "none";

/**
 * Which fallback step drove pricing stats for a titled listing.
 * `fuzzy` = Near step (≥5 nearby). `thin_exact` = 1–4 exact rows preferred over segment.
 */
export type CohortFallbackTier =
  | "exact"
  | "fuzzy"
  | "thin_exact"
  | "segment"
  | "insufficient"
  | "none";

export interface TitleCohortStatsMetadata {
  cohortTier: CohortFallbackTier;
  cohortMatchMode: TitleCohortMatchMode;
  exactCount: number;
  /** Listing rows used for stats when cohort-backed (exact, near, or thin_exact). */
  cohortCompCount: number;
  /** Total rows in the category/type segment before cohort narrowing. */
  segmentCompCount: number;
  cohortUsedForStats: boolean;
}

const COHORT_TIER_RANK: Record<CohortFallbackTier, number> = {
  exact: 5,
  fuzzy: 4,
  thin_exact: 3,
  segment: 2,
  none: 1,
  insufficient: 0,
};

export function emptyTitleCohortStatsMetadata(
  tier: CohortFallbackTier = "insufficient",
): TitleCohortStatsMetadata {
  return {
    cohortTier: tier,
    cohortMatchMode: "none",
    exactCount: 0,
    cohortCompCount: 0,
    segmentCompCount: 0,
    cohortUsedForStats: false,
  };
}

export function resolveCohortFallbackTier(
  titleSignal: TitleCohortSignal | null,
  selection: Pick<
    TitleCohortSelection<unknown>,
    "cohortUsed" | "matchMode" | "exactCount"
  >,
  statsRowCount: number,
): CohortFallbackTier {
  if (titleSignal == null) {
    return statsRowCount > 0 ? "none" : "insufficient";
  }
  if (selection.cohortUsed) {
    if (selection.matchMode === "fuzzy") {
      return "fuzzy";
    }
    if (
      selection.exactCount > 0 &&
      selection.exactCount < MIN_TITLE_COHORT_ITEMS
    ) {
      return "thin_exact";
    }
    return "exact";
  }
  if (statsRowCount === 0) {
    return "insufficient";
  }
  return "segment";
}

export function buildTitleCohortStatsMetadata(
  titleSignal: TitleCohortSignal | null,
  selection: TitleCohortSelection<unknown>,
  segmentRowCount: number,
  statsRowCount: number,
): TitleCohortStatsMetadata {
  const cohortUsedForStats = selection.cohortUsed;
  return {
    cohortTier: resolveCohortFallbackTier(
      titleSignal,
      selection,
      statsRowCount,
    ),
    cohortMatchMode: selection.matchMode,
    exactCount: titleSignal != null ? selection.exactCount : 0,
    cohortCompCount: cohortUsedForStats ? statsRowCount : 0,
    segmentCompCount: segmentRowCount,
    cohortUsedForStats,
  };
}

/** Combined source: prefer the branch with stronger cohort match (Phase 1 preview of D4). */
export function pickStrongerCohortMetadata(
  a: TitleCohortStatsMetadata,
  b: TitleCohortStatsMetadata,
): TitleCohortStatsMetadata {
  const rankA = COHORT_TIER_RANK[a.cohortTier];
  const rankB = COHORT_TIER_RANK[b.cohortTier];
  if (rankA !== rankB) {
    return rankA > rankB ? a : b;
  }
  if (a.cohortCompCount !== b.cohortCompCount) {
    return a.cohortCompCount >= b.cohortCompCount ? a : b;
  }
  if (a.exactCount !== b.exactCount) {
    return a.exactCount >= b.exactCount ? a : b;
  }
  return a;
}

/** Tier-1 cache suffix when fuzzy widening may change stats for capacity signals. */
export function cohortFuzzyCacheSuffix(
  title?: string,
  context?: TitleCohortExtractContext,
): string {
  if (!title?.trim()) return "";
  const signal = extractTitleCohortSignal(title.trim(), context);
  if (signal == null || !supportsFuzzyTitleCohort(signal)) {
    return "";
  }
  return shouldUseFuzzyTitleCohort() ? "_fzy1" : "_fzy0";
}

/** Default on; set `PRICING_TITLE_COHORT_FUZZY=false` to disable capacity widening. */
export function shouldUseFuzzyTitleCohort(): boolean {
  return process.env.PRICING_TITLE_COHORT_FUZZY !== "false";
}

export function supportsFuzzyTitleCohort(signal: TitleCohortSignal): boolean {
  switch (signal.kind) {
    case "liters":
    case "kva":
    case "hp_ac":
    case "kg":
    case "watts":
    case "seater":
    case "fan_inches":
      return true;
    default:
      return false;
  }
}

/** Discriminated union of supported title signals (brand-agnostic). */
export type TitleCohortKind =
  | "display_inches"
  | "kva"
  | "hp_ac"
  | "liters"
  | "bed_ft"
  | "furniture_ft"
  | "kg"
  | "seater"
  | "fan_inches"
  | "watts";

export type TitleCohortSignal =
  | { kind: "display_inches"; value: number }
  | { kind: "kva"; value: number }
  | { kind: "hp_ac"; value: number }
  | { kind: "liters"; value: number }
  | { kind: "bed_ft"; a: number; b: number }
  /** Product L×H / L×W in ft for furniture & decor — not bed size. */
  | { kind: "furniture_ft"; a: number; b: number }
  | { kind: "kg"; value: number }
  /** Total seat count from title (single or summed hyphen chain). */
  | { kind: "seater"; value: number }
  /** Fan blade / size in inches (not TV display diagonal). */
  | { kind: "fan_inches"; value: number }
  /** Audio output power in watts (sound system / speaker / soundbar family). */
  | { kind: "watts"; value: number };

/**
 * Optional extract context. Subcategory scopes attribute meaning — e.g. `4×6`
 * is a bed size only for bedding, not shelf / TV console / wardrobe / rug dims.
 */
export type TitleCohortExtractContext = {
  subcategoryId?: string | null;
};

/** Subcategories where `N x M` / `N by M` means bed/mattress size (ft). */
export const TITLE_COHORT_BED_FT_SUBCATEGORY_IDS = [
  "bed-frame",
  "mattress",
  "bed-frame-mattress",
] as const;

/**
 * Subcategories where `N x M` means product dimensions (ft), not bed size.
 * Furniture surface specs + shelf / cabinets / rugs.
 */
export const TITLE_COHORT_FURNITURE_FT_SUBCATEGORY_IDS = [
  ...FURNITURE_SURFACE_SUBCATEGORY_IDS,
  "shelf",
  "kitchen-cabinet",
  "hanging-kitchen-cabinet",
  "rug-carpet",
] as const;

export function isBedFtTitleCohortSubcategory(
  subcategoryId?: string | null,
): boolean {
  const id = subcategoryId?.trim();
  if (!id) return false;
  return (TITLE_COHORT_BED_FT_SUBCATEGORY_IDS as readonly string[]).includes(
    id,
  );
}

export function isFurnitureFtTitleCohortSubcategory(
  subcategoryId?: string | null,
): boolean {
  const id = subcategoryId?.trim();
  if (!id) return false;
  return (
    TITLE_COHORT_FURNITURE_FT_SUBCATEGORY_IDS as readonly string[]
  ).includes(id);
}

/**
 * Signal-family allowlist (Appendix A — title-cohort comp hygiene).
 * Enforced by `extractTitleCohortSignal`; open extract remains for audit/hygiene.
 */
export const TITLE_COHORT_DISPLAY_INCHES_SUBCATEGORY_IDS = [
  "smart-tv",
  "non-smart-tv",
  "monitor",
] as const;

export const TITLE_COHORT_HP_AC_SUBCATEGORY_IDS = [
  "air-conditioner",
  "inverter-air-conditioner",
] as const;

export const TITLE_COHORT_KVA_SUBCATEGORY_IDS = ["generator"] as const;

/** Fridge / freezer coolers + microwave (Wave-4) — shared `liters` kind. */
export const TITLE_COHORT_LITERS_SUBCATEGORY_IDS = [
  "fridge",
  "chest-freezers",
  "food-cooler",
  "mini-fridge",
  "standing-fridge",
  "inverter-fridge",
  "side-by-side-fridge",
  CANONICAL_MICROWAVE_SUBCATEGORY_ID,
  ...LEGACY_MICROWAVE_SUBCATEGORY_IDS,
] as const;

/** Microwave family only — tighter litre bounds than fridge/freezer. */
export const TITLE_COHORT_MICROWAVE_LITERS_SUBCATEGORY_IDS = [
  CANONICAL_MICROWAVE_SUBCATEGORY_ID,
  ...LEGACY_MICROWAVE_SUBCATEGORY_IDS,
] as const;

export const TITLE_COHORT_KG_SUBCATEGORY_IDS = [
  "washing-machine",
  "inverter-washing-machine",
] as const;

/** Sofa / chair / dining — seating capacity from title (Wave-1 coverage). */
export const TITLE_COHORT_SEATER_SUBCATEGORY_IDS = [
  "sofa-couche",
  "chair",
  "dining-table-chair",
] as const;

/** Fan blade size — canonical + legacy fan aliases (Wave-2 coverage). */
export const TITLE_COHORT_FAN_INCHES_SUBCATEGORY_IDS = [
  CANONICAL_FAN_SUBCATEGORY_ID,
  ...LEGACY_FAN_SUBCATEGORY_IDS,
] as const;

/** Audio power — sound system / speaker / soundbar family (Wave-3). */
export const TITLE_COHORT_WATTS_SUBCATEGORY_IDS = [
  "sound-system",
  "speaker",
  "soundbar",
  "soundbar-speaker",
] as const;

function subcategoryInList(
  subcategoryId: string | null | undefined,
  list: readonly string[],
): boolean {
  const id = subcategoryId?.trim();
  if (!id) return false;
  return list.includes(id);
}

/**
 * Kinds this subcategory may use for title cohorts (empty = fail-closed).
 * Missing/blank `subcategoryId` → no kinds (bed/furniture title heuristics
 * remain extract-time only; they are not “allowed by id”).
 */
export function allowedTitleCohortKindsForSubcategory(
  subcategoryId?: string | null,
): ReadonlySet<TitleCohortKind> {
  const kinds = new Set<TitleCohortKind>();
  if (
    subcategoryInList(
      subcategoryId,
      TITLE_COHORT_DISPLAY_INCHES_SUBCATEGORY_IDS,
    )
  ) {
    kinds.add("display_inches");
  }
  if (subcategoryInList(subcategoryId, TITLE_COHORT_KVA_SUBCATEGORY_IDS)) {
    kinds.add("kva");
  }
  if (subcategoryInList(subcategoryId, TITLE_COHORT_HP_AC_SUBCATEGORY_IDS)) {
    kinds.add("hp_ac");
  }
  if (subcategoryInList(subcategoryId, TITLE_COHORT_LITERS_SUBCATEGORY_IDS)) {
    kinds.add("liters");
  }
  if (subcategoryInList(subcategoryId, TITLE_COHORT_KG_SUBCATEGORY_IDS)) {
    kinds.add("kg");
  }
  if (subcategoryInList(subcategoryId, TITLE_COHORT_SEATER_SUBCATEGORY_IDS)) {
    kinds.add("seater");
  }
  if (
    subcategoryInList(subcategoryId, TITLE_COHORT_FAN_INCHES_SUBCATEGORY_IDS)
  ) {
    kinds.add("fan_inches");
  }
  if (subcategoryInList(subcategoryId, TITLE_COHORT_WATTS_SUBCATEGORY_IDS)) {
    kinds.add("watts");
  }
  if (isBedFtTitleCohortSubcategory(subcategoryId)) {
    kinds.add("bed_ft");
  }
  if (isFurnitureFtTitleCohortSubcategory(subcategoryId)) {
    kinds.add("furniture_ft");
  }
  return kinds;
}

export function isTitleCohortKindAllowedForSubcategory(
  subcategoryId: string | null | undefined,
  kind: TitleCohortKind,
): boolean {
  return allowedTitleCohortKindsForSubcategory(subcategoryId).has(kind);
}

/** Title clearly names bedding when subcategory context is missing. */
function titleSuggestsBeddingProduct(title: string): boolean {
  return /\b(bed|mattress|foam|orthop(?:a)?edic)\b/i.test(title);
}

/** Title names a furniture/decor product that uses L×H-style dimensions. */
function titleSuggestsFurnitureDimProduct(title: string): boolean {
  return /\b(wardrobe|dresser|shelf|shelves|console|cabinet|table|desk|rug|carpet)\b/i.test(
    title,
  );
}

/**
 * Whether to treat dimension pairs as bed_ft.
 * - Bedding subcategory → yes
 * - Other explicit subcategory → no (product dims, not bed size)
 * - Unknown subcategory → only if title names bedding
 */
export function shouldExtractBedFtTitleCohort(
  title: string,
  subcategoryId?: string | null,
): boolean {
  if (isBedFtTitleCohortSubcategory(subcategoryId)) return true;
  const id = subcategoryId?.trim();
  if (id) return false;
  return titleSuggestsBeddingProduct(title);
}

/**
 * Whether to treat dimension pairs as furniture_ft (product size).
 * - Furniture/decor dim subcategory → yes
 * - Explicit other subcategory → no
 * - Unknown subcategory → furniture keywords, and not bedding
 */
export function shouldExtractFurnitureFtTitleCohort(
  title: string,
  subcategoryId?: string | null,
): boolean {
  if (isFurnitureFtTitleCohortSubcategory(subcategoryId)) return true;
  const id = subcategoryId?.trim();
  if (id) return false;
  if (titleSuggestsBeddingProduct(title)) return false;
  return titleSuggestsFurnitureDimProduct(title);
}

function roundKey(n: number): string {
  return Number.isInteger(n) ? String(n) : String(n).replace(".", "p");
}

/** Short human label for UI and logs. */
export function formatTitleCohortSignal(signal: TitleCohortSignal): string {
  switch (signal.kind) {
    case "display_inches":
      return `${signal.value}″ display`;
    case "kva":
      return `${signal.value} KVA`;
    case "hp_ac":
      return `${signal.value} HP`;
    case "liters":
      return `${signal.value} L`;
    case "bed_ft":
      return `${signal.a}×${signal.b} ft bed`;
    case "furniture_ft":
      return `${signal.a}×${signal.b} ft`;
    case "kg":
      return `${signal.value} kg`;
    case "seater":
      return `${signal.value}-seater`;
    case "fan_inches":
      return `${signal.value}″ fan`;
    case "watts":
      return `${signal.value}W`;
    default: {
      const _exhaustive: never = signal;
      return _exhaustive;
    }
  }
}

/**
 * Stable cache segment when a title may imply a cohort (tier-1 / tier-2 keys).
 */
export function cohortCacheSegmentFromTitle(
  title?: string,
  context?: TitleCohortExtractContext,
): string {
  const signal = extractTitleCohortSignal(title, context);
  if (!signal) return "all";
  return cohortSegmentKeyFromSignal(signal);
}

export function cohortSegmentKeyFromSignal(signal: TitleCohortSignal): string {
  switch (signal.kind) {
    case "display_inches":
      return `sz${signal.value}`;
    case "kva":
      return `kv${roundKey(signal.value)}`;
    case "hp_ac":
      return `hp${roundKey(signal.value)}`;
    case "liters":
      return `L${Math.round(signal.value)}`;
    case "bed_ft":
      return `bd${roundKey(signal.a)}x${roundKey(signal.b)}`;
    case "furniture_ft":
      return `ft${roundKey(signal.a)}x${roundKey(signal.b)}`;
    case "kg":
      return `kg${roundKey(signal.value)}`;
    case "seater":
      return `st${signal.value}`;
    case "fan_inches":
      return `fi${signal.value}`;
    case "watts":
      return `w${signal.value}`;
    default: {
      const _exhaustive: never = signal;
      return _exhaustive;
    }
  }
}

/**
 * Extract display diagonal in inches (TVs, monitors, some fans with "26 inches" in title).
 */
export function extractDisplaySizeInchesFromTitle(
  title: string,
): number | null {
  const t = title.trim();
  if (!t) return null;

  const patterns: RegExp[] = [
    /\b(\d{2,3})\s*(?:inch|inches|in\.)\b/i,
    // Sellers often abbreviate inches as "In" (e.g. "55 In Hisense Smart TVs")
    /\b(\d{2,3})\s+in\b/i,
    /\b(\d{2,3})\s*["\u201c\u201d]/,
    /\b(\d{2,3})(?:inch|inches)\b/i,
    // TV listings often omit "inch" (e.g. "Hisense 55 Smart TV", "Samsung 55 QLED")
    /\b(\d{2,3})\s+smart\s*tv\b/i,
    /\b(\d{2,3})\s+smart\b/i,
    /\b(\d{2,3})\s+(?:led|oled|qled|uhd)\b/i,
  ];
  for (const re of patterns) {
    const m = t.match(re);
    if (m) {
      const n = parseInt(m[1], 10);
      if (n >= 15 && n <= 120) return n;
    }
  }
  return null;
}

function extractKvaFromTitle(title: string): number | null {
  const m = title.match(/\b(\d+(?:\.\d+)?)\s*kva\b/i);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (n > 0 && n <= 500) return n;
  return null;
}

/** e.g. 4.5 by 6, 6 x 6, 4.5/6 mattress */
function extractBedFtFromTitle(title: string): { a: number; b: number } | null {
  const pair = extractDimensionPairFromTitle(title);
  if (!pair) return null;
  const { a, b } = pair;
  if (a < 2 || b < 2 || a > 12 || b > 12) return null;
  return { a, b };
}

/**
 * Furniture / decor product dimensions in ft (L×H or L×W).
 * Accepts optional ft markers: "6ft by 3ft", "10 x 8 Ft", "6.5ft×3ft".
 */
function extractFurnitureFtFromTitle(
  title: string,
): { a: number; b: number } | null {
  const pair = extractDimensionPairFromTitle(title);
  if (!pair) return null;
  const { a, b } = pair;
  // Align with furniture surface length/width/height ft bounds (1–20 / 1–10).
  if (a < 1 || b < 1 || a > 20 || b > 20) return null;
  if (Math.max(a, b) > 20) return null;
  return { a, b };
}

/** Shared `N (ft)? × M (ft)?` pair parser — meaning decided by subcategory gate. */
function extractDimensionPairFromTitle(
  title: string,
): { a: number; b: number } | null {
  const re =
    /\b(\d+(?:\.\d+)?)\s*(?:ft|feet|')?\s*(?:by|x|X|×|\/)\s*(\d+(?:\.\d+)?)\s*(?:ft|feet|')?\b/i;
  const m = title.match(re);
  if (!m) return null;
  const a = parseFloat(m[1]);
  const b = parseFloat(m[2]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return { a, b };
}

function extractHpFromTitle(title: string): number | null {
  const m = title.match(/\b(\d+(?:\.\d+)?)\s*hp\b/i);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (n > 0 && n <= 20) return n;
  return null;
}

function litersBoundsForSubcategory(subcategoryId?: string | null): {
  min: number;
  max: number;
} {
  if (
    subcategoryInList(
      subcategoryId,
      TITLE_COHORT_MICROWAVE_LITERS_SUBCATEGORY_IDS,
    )
  ) {
    return {
      min: TITLE_COHORT_MICROWAVE_LITERS_MIN,
      max: TITLE_COHORT_MICROWAVE_LITERS_MAX,
    };
  }
  return { min: TITLE_COHORT_LITERS_MIN, max: TITLE_COHORT_LITERS_MAX };
}

/**
 * Fridge / freezer / microwave capacities (e.g. 203L, 20 L, 25 litres).
 * Microwave uses tighter bounds when `subcategoryId` is in the microwave family.
 */
function extractLitersFromTitle(
  title: string,
  subcategoryId?: string | null,
): number | null {
  const t = title.trim();
  if (!t) return null;

  // Ranges like "9 to 13 liters" — no single cohort value.
  if (/\b[\d,]+\s+to\s+[\d,]+\s*(?:l\b|litres?|liters?)/i.test(t)) {
    return null;
  }

  const lToken = t.match(/\b([\d,]+)\s*L\b/i);
  const word = t.match(/\b([\d,]+)\s*(?:litres?|liters?)\b/i);
  const raw = lToken?.[1] ?? word?.[1];
  if (!raw) return null;

  const n = parseInt(raw.replace(/,/g, ""), 10);
  if (!Number.isFinite(n)) return null;

  const { min, max } = litersBoundsForSubcategory(subcategoryId);
  if (n >= min && n <= max) return n;
  return null;
}

function extractKgFromTitle(title: string): number | null {
  const m = title.match(/\b(\d+(?:\.\d+)?)\s*kg\b/i);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (n > 0 && n <= 200) return n;
  return null;
}

const SEATER_WORD = "(?:seater|sitters?|seats?)";

/**
 * Total seating capacity from title.
 * Hyphen chains before the seat word (`2-2 Seater`, `2-1-1 Seater`) sum to one N.
 */
export function extractSeaterFromTitle(title: string): number | null {
  const t = title.trim();
  if (!t) return null;

  const chain = t.match(
    new RegExp(`\\b(\\d+(?:\\s*-\\s*\\d+)+)\\s*-?\\s*${SEATER_WORD}\\b`, "i"),
  );
  if (chain) {
    const parts = chain[1].split(/\s*-\s*/).map((p) => parseInt(p, 10));
    if (parts.some((n) => !Number.isFinite(n) || n < 1)) return null;
    const sum = parts.reduce((a, b) => a + b, 0);
    if (sum < TITLE_COHORT_SEATER_MIN || sum > TITLE_COHORT_SEATER_MAX) {
      return null;
    }
    return sum;
  }

  const single = t.match(
    new RegExp(`\\b(\\d+)\\s*-?\\s*${SEATER_WORD}\\b`, "i"),
  );
  if (!single) return null;
  const n = parseInt(single[1], 10);
  if (
    !Number.isFinite(n) ||
    n < TITLE_COHORT_SEATER_MIN ||
    n > TITLE_COHORT_SEATER_MAX
  ) {
    return null;
  }
  return n;
}

/**
 * Fan blade / size inches from title.
 * Separate from TV `display_inches` (no smart-TV heuristics; blade bounds 6–60).
 */
export function extractFanInchesFromTitle(title: string): number | null {
  const t = title.trim();
  if (!t) return null;

  const patterns: RegExp[] = [
    /\b(\d{1,2})\s*(?:inch|inches|in\.)\b/i,
    /\b(\d{1,2})\s+in\b/i,
    /\b(\d{1,2})\s*["\u201c\u201d]/,
    /\b(\d{1,2})(?:inch|inches)\b/i,
    // Composer style: "18In Standing Fan"
    /\b(\d{1,2})in\b/i,
  ];
  for (const re of patterns) {
    const m = t.match(re);
    if (!m) continue;
    const n = parseInt(m[1], 10);
    if (
      Number.isFinite(n) &&
      n >= FAN_BLADE_SIZE_INCHES_MIN &&
      n <= FAN_BLADE_SIZE_INCHES_MAX
    ) {
      return n;
    }
  }
  return null;
}

const WATT_WORD = "(?:W|watts?)";

/**
 * Audio output power (watts) from title.
 * Requires an explicit W/watt token — rejects bare numbers and channel decimals (2.1).
 * Optional thousands commas (`10,000W` → 10000).
 */
export function extractWattsFromTitle(title: string): number | null {
  const t = title.trim();
  if (!t) return null;

  const m = t.match(new RegExp(`\\b([\\d,]+)\\s*${WATT_WORD}\\b`, "i"));
  if (!m) return null;
  const n = parseInt(m[1].replace(/,/g, ""), 10);
  if (
    !Number.isFinite(n) ||
    n < TITLE_COHORT_WATTS_MIN ||
    n > TITLE_COHORT_WATTS_MAX
  ) {
    return null;
  }
  return n;
}

/**
 * Open priority extract (no Appendix A gate). Used by mismatch audit + hygiene
 * cleanup (D3) so disallowed kinds are still detectable.
 *
 * Pass `context.subcategoryId` when known so bed dimensions are not confused
 * with furniture / decor product dimensions.
 */
export function extractTitleCohortSignalOpen(
  title: string | undefined,
  context?: TitleCohortExtractContext,
): TitleCohortSignal | null {
  if (!title?.trim()) return null;
  const t = title.trim();
  const subcategoryId = context?.subcategoryId;

  // Fan family: prefer blade inches over TV display heuristics (hygiene / open audit).
  if (
    subcategoryInList(subcategoryId, TITLE_COHORT_FAN_INCHES_SUBCATEGORY_IDS)
  ) {
    const fanInches = extractFanInchesFromTitle(t);
    if (fanInches != null) {
      return { kind: "fan_inches", value: fanInches };
    }
  }

  const inches = extractDisplaySizeInchesFromTitle(t);
  if (inches != null) {
    return { kind: "display_inches", value: inches };
  }

  const kva = extractKvaFromTitle(t);
  if (kva != null) {
    return { kind: "kva", value: kva };
  }

  if (shouldExtractBedFtTitleCohort(t, subcategoryId)) {
    const bed = extractBedFtFromTitle(t);
    if (bed != null) {
      return { kind: "bed_ft", a: bed.a, b: bed.b };
    }
  }

  if (shouldExtractFurnitureFtTitleCohort(t, subcategoryId)) {
    const furniture = extractFurnitureFtFromTitle(t);
    if (furniture != null) {
      return { kind: "furniture_ft", a: furniture.a, b: furniture.b };
    }
  }

  const hp = extractHpFromTitle(t);
  if (hp != null) {
    return { kind: "hp_ac", value: hp };
  }

  const liters = extractLitersFromTitle(t, subcategoryId);
  if (liters != null) {
    return { kind: "liters", value: liters };
  }

  const kg = extractKgFromTitle(t);
  if (kg != null) {
    return { kind: "kg", value: kg };
  }

  const seater = extractSeaterFromTitle(t);
  if (seater != null) {
    return { kind: "seater", value: seater };
  }

  const watts = extractWattsFromTitle(t);
  if (watts != null) {
    return { kind: "watts", value: watts };
  }

  const fanInches = extractFanInchesFromTitle(t);
  if (fanInches != null) {
    return { kind: "fan_inches", value: fanInches };
  }

  return null;
}

function mayUseTitleCohortKind(
  kind: TitleCohortKind,
  subcategoryId?: string | null,
): boolean {
  const id = subcategoryId?.trim();
  if (!id) {
    // D7: unknown subcategory → capacity/display fail-closed; bed/furniture via heuristics.
    return kind === "bed_ft" || kind === "furniture_ft";
  }
  return isTitleCohortKindAllowedForSubcategory(id, kind);
}

/**
 * Production cohort extract: Appendix A allowlist by subcategory family.
 * Tries the same priority order as the open extractor, skipping disallowed kinds
 * so a later allowed signal can still win (e.g. inches then litres on a fridge).
 */
export function extractTitleCohortSignal(
  title: string | undefined,
  context?: TitleCohortExtractContext,
): TitleCohortSignal | null {
  if (!title?.trim()) return null;
  const t = title.trim();
  const subcategoryId = context?.subcategoryId;

  if (mayUseTitleCohortKind("display_inches", subcategoryId)) {
    const inches = extractDisplaySizeInchesFromTitle(t);
    if (inches != null) {
      return { kind: "display_inches", value: inches };
    }
  }

  if (mayUseTitleCohortKind("fan_inches", subcategoryId)) {
    const fanInches = extractFanInchesFromTitle(t);
    if (fanInches != null) {
      return { kind: "fan_inches", value: fanInches };
    }
  }

  if (mayUseTitleCohortKind("kva", subcategoryId)) {
    const kva = extractKvaFromTitle(t);
    if (kva != null) {
      return { kind: "kva", value: kva };
    }
  }

  if (
    mayUseTitleCohortKind("bed_ft", subcategoryId) &&
    shouldExtractBedFtTitleCohort(t, subcategoryId)
  ) {
    const bed = extractBedFtFromTitle(t);
    if (bed != null) {
      return { kind: "bed_ft", a: bed.a, b: bed.b };
    }
  }

  if (
    mayUseTitleCohortKind("furniture_ft", subcategoryId) &&
    shouldExtractFurnitureFtTitleCohort(t, subcategoryId)
  ) {
    const furniture = extractFurnitureFtFromTitle(t);
    if (furniture != null) {
      return { kind: "furniture_ft", a: furniture.a, b: furniture.b };
    }
  }

  if (mayUseTitleCohortKind("hp_ac", subcategoryId)) {
    const hp = extractHpFromTitle(t);
    if (hp != null) {
      return { kind: "hp_ac", value: hp };
    }
  }

  if (mayUseTitleCohortKind("liters", subcategoryId)) {
    const liters = extractLitersFromTitle(t, subcategoryId);
    if (liters != null) {
      return { kind: "liters", value: liters };
    }
  }

  if (mayUseTitleCohortKind("kg", subcategoryId)) {
    const kg = extractKgFromTitle(t);
    if (kg != null) {
      return { kind: "kg", value: kg };
    }
  }

  if (mayUseTitleCohortKind("seater", subcategoryId)) {
    const seater = extractSeaterFromTitle(t);
    if (seater != null) {
      return { kind: "seater", value: seater };
    }
  }

  if (mayUseTitleCohortKind("watts", subcategoryId)) {
    const watts = extractWattsFromTitle(t);
    if (watts != null) {
      return { kind: "watts", value: watts };
    }
  }

  return null;
}

export function titleMatchesDisplaySizeInches(
  itemTitle: string | null | undefined,
  inches: number,
): boolean {
  if (!itemTitle) return false;
  const t = itemTitle;
  const word = new RegExp(
    `\\b${inches}\\s*(?:inch|inches|in\\.)(?:\\b|$)`,
    "i",
  );
  if (word.test(t)) return true;
  const abbreviatedIn = new RegExp(`\\b${inches}\\s+in\\b`, "i");
  if (abbreviatedIn.test(t)) return true;
  const glued = new RegExp(`\\b${inches}(?:inch|inches)\\b`, "i");
  if (glued.test(t)) return true;
  const quote = new RegExp(`\\b${inches}\\s*["\u201c\u201d]`);
  if (quote.test(t)) return true;
  const smartTv = new RegExp(`\\b${inches}\\s+smart\\s*tv\\b`, "i");
  if (smartTv.test(t)) return true;
  const smart = new RegExp(`\\b${inches}\\s+smart\\b`, "i");
  if (smart.test(t)) return true;
  const panel = new RegExp(`\\b${inches}\\s+(?:led|oled|qled|uhd)\\b`, "i");
  return panel.test(t);
}

function approxEq(a: number, b: number, eps = 0.01): boolean {
  return Math.abs(a - b) <= eps;
}

function titleMatchesKva(
  itemTitle: string | null | undefined,
  kva: number,
  toleranceKva = 0.05,
): boolean {
  if (!itemTitle) return false;
  const found = extractKvaFromTitle(itemTitle);
  return found != null && Math.abs(found - kva) <= toleranceKva;
}

function titleMatchesHp(
  itemTitle: string | null | undefined,
  hp: number,
  toleranceHp = 0.05,
): boolean {
  if (!itemTitle) return false;
  const found = extractHpFromTitle(itemTitle);
  return found != null && Math.abs(found - hp) <= toleranceHp;
}

function titleMatchesLiters(
  itemTitle: string | null | undefined,
  liters: number,
  toleranceL = 0,
): boolean {
  if (!itemTitle) return false;
  const found = extractLitersFromTitle(itemTitle);
  if (found == null) return false;
  if (toleranceL <= 0) {
    return Math.round(found) === Math.round(liters);
  }
  return Math.abs(found - liters) <= toleranceL;
}

function titleMatchesBedFt(
  itemTitle: string | null | undefined,
  a: number,
  b: number,
): boolean {
  if (!itemTitle) return false;
  const found = extractBedFtFromTitle(itemTitle);
  if (!found) return false;
  return dimensionPairsMatch(found.a, found.b, a, b);
}

function titleMatchesFurnitureFt(
  itemTitle: string | null | undefined,
  a: number,
  b: number,
): boolean {
  if (!itemTitle) return false;
  const found = extractFurnitureFtFromTitle(itemTitle);
  if (!found) return false;
  return dimensionPairsMatch(found.a, found.b, a, b);
}

function dimensionPairsMatch(
  foundA: number,
  foundB: number,
  a: number,
  b: number,
): boolean {
  const pairs = [
    [foundA, foundB],
    [foundB, foundA],
  ];
  return pairs.some(([x, y]) => approxEq(x, a, 0.05) && approxEq(y, b, 0.05));
}

function titleMatchesKg(
  itemTitle: string | null | undefined,
  kg: number,
  toleranceKg = 0.05,
): boolean {
  if (!itemTitle) return false;
  const found = extractKgFromTitle(itemTitle);
  return found != null && Math.abs(found - kg) <= toleranceKg;
}

function titleMatchesSeater(
  itemTitle: string | null | undefined,
  seats: number,
  tolerance = 0,
): boolean {
  if (!itemTitle) return false;
  const found = extractSeaterFromTitle(itemTitle);
  return found != null && Math.abs(found - seats) <= tolerance;
}

function titleMatchesFanInches(
  itemTitle: string | null | undefined,
  inches: number,
  tolerance = 0,
): boolean {
  if (!itemTitle) return false;
  const found = extractFanInchesFromTitle(itemTitle);
  return found != null && Math.abs(found - inches) <= tolerance;
}

function titleMatchesWatts(
  itemTitle: string | null | undefined,
  watts: number,
  relativeTolerance = 0,
): boolean {
  if (!itemTitle) return false;
  const found = extractWattsFromTitle(itemTitle);
  if (found == null) return false;
  if (relativeTolerance <= 0) {
    return found === watts;
  }
  if (watts <= 0) return false;
  return Math.abs(found - watts) / watts <= relativeTolerance;
}

function fuzzyToleranceForSignal(signal: TitleCohortSignal): number | null {
  switch (signal.kind) {
    case "liters":
      return TITLE_COHORT_FUZZY_LITERS_TOLERANCE;
    case "kva":
      return TITLE_COHORT_FUZZY_KVA_TOLERANCE;
    case "hp_ac":
      return TITLE_COHORT_FUZZY_HP_TOLERANCE;
    case "kg":
      return TITLE_COHORT_FUZZY_KG_TOLERANCE;
    case "watts":
      return TITLE_COHORT_NEAR_WATTS_RELATIVE;
    case "seater":
      return TITLE_COHORT_NEAR_SEATER_TOLERANCE;
    case "fan_inches":
      return TITLE_COHORT_NEAR_FAN_INCHES_TOLERANCE;
    default:
      return null;
  }
}

/** Human-readable tolerance for UI copy (e.g. "±10 L"). */
export function formatTitleCohortFuzzyTolerance(
  signal: TitleCohortSignal,
): string | null {
  const tolerance = fuzzyToleranceForSignal(signal);
  if (tolerance == null) return null;
  switch (signal.kind) {
    case "liters":
      return `±${tolerance} L`;
    case "kva":
      return `±${tolerance} KVA`;
    case "hp_ac":
      return `±${tolerance} HP`;
    case "kg":
      return `±${tolerance} kg`;
    case "watts":
      return `±${Math.round(tolerance * 100)}%`;
    case "seater":
      return `±${tolerance} seat`;
    case "fan_inches":
      return `±${tolerance}″`;
    default:
      return null;
  }
}

/** Whether a comp title matches the query signal within fuzzy / Near capacity buckets. */
export function titleMatchesCohortSignalFuzzy(
  itemTitle: string | null | undefined,
  signal: TitleCohortSignal,
): boolean {
  if (!itemTitle) return false;
  const tolerance = fuzzyToleranceForSignal(signal);
  if (tolerance == null) {
    return titleMatchesCohortSignal(itemTitle, signal);
  }
  switch (signal.kind) {
    case "liters":
      return titleMatchesLiters(itemTitle, signal.value, tolerance);
    case "kva":
      return titleMatchesKva(itemTitle, signal.value, tolerance);
    case "hp_ac":
      return titleMatchesHp(itemTitle, signal.value, tolerance);
    case "kg":
      return titleMatchesKg(itemTitle, signal.value, tolerance);
    case "watts":
      return titleMatchesWatts(itemTitle, signal.value, tolerance);
    case "seater":
      return titleMatchesSeater(itemTitle, signal.value, tolerance);
    case "fan_inches":
      return titleMatchesFanInches(itemTitle, signal.value, tolerance);
    default:
      return titleMatchesCohortSignal(itemTitle, signal);
  }
}

export interface TitleCohortSelection<T> {
  rows: T[];
  cohortUsed: boolean;
  matchMode: TitleCohortMatchMode;
  exactCount: number;
}

/**
 * Soft-widen ladder (D2): exact ≥5 → near ≥5 → thin exact (1–4) → segment.
 */
export function selectRowsForTitleCohort<T>(
  rows: T[],
  titleSignal: TitleCohortSignal | null,
  getTitle: (row: T) => string | null | undefined,
): TitleCohortSelection<T> {
  if (titleSignal == null) {
    return { rows, cohortUsed: false, matchMode: "none", exactCount: 0 };
  }

  const exact = rows.filter((row) =>
    titleMatchesCohortSignal(getTitle(row), titleSignal),
  );
  if (exact.length >= MIN_TITLE_COHORT_ITEMS) {
    return {
      rows: exact,
      cohortUsed: true,
      matchMode: "exact",
      exactCount: exact.length,
    };
  }

  if (shouldUseFuzzyTitleCohort() && supportsFuzzyTitleCohort(titleSignal)) {
    const fuzzy = rows.filter((row) =>
      titleMatchesCohortSignalFuzzy(getTitle(row), titleSignal),
    );
    if (fuzzy.length >= MIN_TITLE_COHORT_ITEMS) {
      return {
        rows: fuzzy,
        cohortUsed: true,
        matchMode: "fuzzy",
        exactCount: exact.length,
      };
    }
  }

  if (exact.length >= 1) {
    return {
      rows: exact,
      cohortUsed: true,
      matchMode: "exact",
      exactCount: exact.length,
    };
  }

  return {
    rows,
    cohortUsed: false,
    matchMode: "none",
    exactCount: exact.length,
  };
}

/** Rationale fragment for aggregate stats when a title cohort was applied. */
export function formatTitleCohortMatchPhrase(
  signal: TitleCohortSignal,
  matchMode: TitleCohortMatchMode,
): string {
  const base = formatTitleCohortSignal(signal);
  if (matchMode !== "fuzzy") {
    return base;
  }
  const tolerance = formatTitleCohortFuzzyTolerance(signal);
  return tolerance != null ? `approximately ${base} (${tolerance})` : base;
}

/** Whether a listing title matches the same cohort signal as the query. */
export function titleMatchesCohortSignal(
  itemTitle: string | null | undefined,
  signal: TitleCohortSignal,
): boolean {
  if (!itemTitle) return false;
  switch (signal.kind) {
    case "display_inches":
      return titleMatchesDisplaySizeInches(itemTitle, signal.value);
    case "kva":
      return titleMatchesKva(itemTitle, signal.value);
    case "hp_ac":
      return titleMatchesHp(itemTitle, signal.value);
    case "liters":
      return titleMatchesLiters(itemTitle, signal.value);
    case "bed_ft":
      return titleMatchesBedFt(itemTitle, signal.a, signal.b);
    case "furniture_ft":
      return titleMatchesFurnitureFt(itemTitle, signal.a, signal.b);
    case "kg":
      return titleMatchesKg(itemTitle, signal.value);
    case "seater":
      return titleMatchesSeater(itemTitle, signal.value);
    case "fan_inches":
      return titleMatchesFanInches(itemTitle, signal.value);
    case "watts":
      return titleMatchesWatts(itemTitle, signal.value);
    default: {
      const _exhaustive: never = signal;
      return _exhaustive;
    }
  }
}

/** Admin comps UI — exact cohort rows vs fuzzy-only neighbors (Phase 4). */
export function splitTitleCohortDisplayRows<T>(
  pool: T[],
  signal: TitleCohortSignal,
  getTitle: (row: T) => string | null | undefined,
): { exactRows: T[]; nearbyRows: T[]; exactCount: number } {
  const exactRows = pool.filter((row) =>
    titleMatchesCohortSignal(getTitle(row), signal),
  );
  const nearbyRows =
    shouldUseFuzzyTitleCohort() && supportsFuzzyTitleCohort(signal)
      ? pool.filter(
          (row) =>
            titleMatchesCohortSignalFuzzy(getTitle(row), signal) &&
            !titleMatchesCohortSignal(getTitle(row), signal),
        )
      : [];
  return { exactRows, nearbyRows, exactCount: exactRows.length };
}
